import React, { useEffect, useRef, useState } from 'react';
import { flightSearchService } from '../../../services/flightSearchService';

// Typeahead for an airport/city IATA code - used by FlightSearchModal.js's
// "From"/"To" fields. Agent can type a city name (e.g. "Orlando") or a
// partial airport name/code and pick a suggestion, or just type a 3-letter
// IATA code directly (no suggestion required to submit the search).
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const AirportAutocompleteInput = ({ label, value, onChange, placeholder, required }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const blurTimeoutRef = useRef(null);

  // Keep the visible text in sync if the parent resets/clears the field.
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
      clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const fetchSuggestions = (text) => {
    clearTimeout(debounceRef.current);

    if (text.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await flightSearchService.getAirportSuggestions(text.trim());
        setSuggestions(response.data?.suggestions || []);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleInputChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    setShowSuggestions(true);
    // Mirror the raw typed text up to the parent as-is (no case-forcing
    // here) - FlightSearchModal decides at submit time whether this looks
    // like an IATA code or needs server-side name resolution.
    onChange(text);
    fetchSuggestions(text);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.iataCode);
    onChange(suggestion.iataCode);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div className="form-group airport-autocomplete">
      <label>{label}</label>
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => setShowSuggestions(true)}
        onBlur={() => {
          // Delay so a click on a suggestion registers before we hide the list.
          blurTimeoutRef.current = setTimeout(() => setShowSuggestions(false), 150);
        }}
        placeholder={placeholder}
        maxLength={40}
        required={required}
        autoComplete="off"
      />
      {showSuggestions && (loading || suggestions.length > 0) && (
        <ul className="airport-suggestions-list">
          {loading && <li className="airport-suggestion-loading">Searching...</li>}
          {!loading && suggestions.map((s) => (
            <li key={`${s.type}-${s.iataCode}`} onClick={() => handleSelect(s)}>
              <span className="airport-suggestion-code">{s.iataCode}</span>
              <span className="airport-suggestion-name">
                {s.type === 'city' ? s.cityName : `${s.name}${s.cityName ? `, ${s.cityName}` : ''}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AirportAutocompleteInput;
